import { getGroupType } from 'features/signUpSlice'
import AccountSetupLayout from 'layouts/AccountSetupLayout'
import ProgressBar from 'layouts/ProgressBar/ProgressBar'
import { useIntl } from 'react-intl'
import { Redirect, Route, Switch } from 'react-router-dom'
import { GroupTypes } from 'utils/constants/groups'
import { useAppSelector } from 'utils/hooks/reduxToolkit'
import Separator from 'ui/Separator'
import {
  BillingAccount,
  Payment,
  PaymentCheckStep,
  SetupAccount,
} from '../GetStarted'
import SignupPlanSelector from '../InvestorAccountSetup/SignupPlanSelector'
import { useInvestorAccountSetup } from '../InvestorAccountSetup/useInvestorAccountSetup'
import * as Styles from './SignupWizard.styles'
import CreateGroup from '../GetStarted/components/CreateGroup'
import FounderGroupSetup from '../FounderGroupSetup'

enum SignupSteps {
  WELCOME = 'welcome',
  ADD_USER_INFO = 'add_user_info',
  ADD_BILLING_INFO = 'add_billing_info',
  ADD_PAYMENT_DETAILS = 'add_payment_details',
  SETUP_GROUP = 'SETUP_GROUP',
  INVITE = 'invite',
}

const SignupWizard = () => {
  const intl = useIntl()
  const {
    urlPrefix,
    clientSecret,
    paymentError,
    isUpdatingUserInfo,
    userAddedFromGroupManagement,
    onSetupAccount,
    onClickContinueToPayment,
    onPaymentSuccess,
    goBackToPayment,
    onContinueForUserAddedFromGroupManagement,
  } = useInvestorAccountSetup()

  const groupType = useAppSelector(getGroupType)

  const getCurrentStep = () => {
    const path = window.location.pathname

    if (path.includes('select-plan')) return SignupSteps.WELCOME
    if (path.includes('account-setup')) return SignupSteps.ADD_USER_INFO
    if (path.includes('group-setup')) return SignupSteps.SETUP_GROUP
    if (path.includes('founder-setup')) return SignupSteps.SETUP_GROUP
    if (path.includes('billing')) return SignupSteps.ADD_BILLING_INFO
    if (path.includes('payment')) return SignupSteps.ADD_PAYMENT_DETAILS
    if (path.includes('invite')) return SignupSteps.INVITE
    return SignupSteps.WELCOME
  }

  const steps = [
    {
      id: SignupSteps.WELCOME,
      label: intl.formatMessage({
        id: 'signup.steps.welcome',
      }),
    },
    {
      id: SignupSteps.ADD_USER_INFO,
      label: intl.formatMessage({
        id: 'signup.steps.addUserInfo',
      }),
    },
    {
      id: SignupSteps.SETUP_GROUP,
      label: intl.formatMessage({
        id: 'signup.steps.setupYourGroup',
      }),
    },
    {
      id: SignupSteps.ADD_BILLING_INFO,
      label: intl.formatMessage({
        id: 'signup.steps.addBillingInfo',
      }),
      hidden: groupType === GroupTypes.FOUNDER,
    },
    {
      id: SignupSteps.ADD_PAYMENT_DETAILS,
      label: intl.formatMessage({
        id: 'signup.steps.addPaymentDetails',
      }),
      hidden: groupType === GroupTypes.FOUNDER,
    },
  ].filter((step) => !step.hidden)

  return (
    <Styles.Modal show onHide={() => {}} blurOverlay>
      <Styles.SignupWizardContainer>
        <AccountSetupLayout.StickyHeader>
          <AccountSetupLayout.Header />
          {getCurrentStep() !== SignupSteps.INVITE && (
            <ProgressBar steps={steps} currentStep={getCurrentStep()} />
          )}
        </AccountSetupLayout.StickyHeader>

        <div>
          <Separator space="3.6rem" />
          <Switch>
            <Route path={`${urlPrefix}/select-plan`}>
              <SignupPlanSelector />
            </Route>

            <Route path={`${urlPrefix}/account-setup`}>
              <SetupAccount
                onClickContinue={onSetupAccount}
                userAddedFromGroupManagement={false}
                isUpdatingUserInfo={isUpdatingUserInfo}
              />
            </Route>

            <Route path={`${urlPrefix}/group-setup`}>
              <CreateGroup />
            </Route>

            <Route path={`${urlPrefix}/founder-setup`}>
              <FounderGroupSetup />
            </Route>

            <Route path={`${urlPrefix}/billing`}>
              <BillingAccount onSubmit={onClickContinueToPayment} />
            </Route>

            <Route path={`${urlPrefix}/payment`}>
              <Payment
                onPaymentSuccess={onPaymentSuccess}
                clientSecret={clientSecret!}
                paymentError={paymentError}
              />
            </Route>

            <Route path={`${urlPrefix}/payment-check`}>
              <PaymentCheckStep
                onPaymentSuccess={onPaymentSuccess}
                goBackToPayment={goBackToPayment}
              />
            </Route>

            <Route path={`${urlPrefix}/invite`}>
              <SetupAccount
                userAddedFromGroupManagement
                onClickContinue={onContinueForUserAddedFromGroupManagement}
                isUpdatingUserInfo={isUpdatingUserInfo}
              />
            </Route>

            <Redirect
              to={`${urlPrefix}/${
                userAddedFromGroupManagement ? 'invite' : 'select-plan'
              }`}
            />
          </Switch>
        </div>
      </Styles.SignupWizardContainer>
    </Styles.Modal>
  )
}

export default SignupWizard
